import { Model } from '@vuex-orm/core';
import Section from '@/models/Section';
import Sectionable from '@/models/Sectionable';
import Date from '@/models/Date';
import Illustration from '@/models/Illustration';
import Image from '@/models/Image';
import Imageable from '@/models/Imageable';
import Project from '@/models/Project';
import Projectable from '@/models/Projectable';

export default class Selector extends Model {
  static entity = 'selectors';

  static state() {
    return {
      viewType: 'list',
      showBanner: false,
      projectId: 0,
    };
  }

  static fields() {
    return {
      id: this.uid(),
      name: this.attr(''),

      singleSection: this.boolean(true),

      section_ids: this.attr(null),
      overview_illustration_ids: this.attr(null),
      section_illustration_ids: this.attr(null),

      project_id: this.attr(null),
      //projects: this.attr(null),

      // View Settings
      showIllustration: this.boolean(true),
      listType: this.attr(null),
      cardImageSize: this.attr(null),

      showPriceOnSold: this.boolean(false),
      showPriceOnReserved: this.boolean(false),

      showColorOnSectionForSale: this.boolean(false),
      showColorOnResidenceForSale: this.boolean(false),

      colorLoader: this.attr(null),
      colorButtons: this.attr(null),
      colorButtonText: this.attr(null),
      colorForSale: this.attr(null),
      colorReserved: this.attr(null),
      colorSold: this.attr(null),
      colorTextColor: this.attr(null),

      // Options
      hasOptions: this.boolean(false),

      // Calculator
      hasCalculator: this.boolean(false),

      interestRateIndividualSelect: this.boolean(false),
      interestRateIndividual: this.number(0),
      individualLoanDescription: this.attr(null),

      depositSize: this.number(0),
      depositDescription: this.attr(null),
      communalLoanDescription: this.attr(null),
      communalLoanYears: this.number(30),

      interestRateComunal: this.number(0),
      interestFee: this.number(0),
      interestRateComunalDescription: this.attr(null),

      interestRateComunalExtras: this.attr([]),

      installmentPeriods: this.attr(null),

      fixedMonthlyCosts: this.number(0),
      flexibleMonthlyCosts: this.number(0),
      monthlyCostsDescription: this.attr(null),

      taxDeductionCheck: this.boolean(false),
      taxDeductionValue: this.number(0),
      taxDeductionDescription: this.attr(null),

      // Contact Form
      hasContactForm: this.boolean(false),
      //contactForm: this.attr(null),
      contactFormEndpoint: this.attr(null),
      contactFormClientIds: this.attr(null),
      contactFormProjectIds: this.attr(null),
      contactFormEmailRecipients: this.attr(null),
      contactFormTitle: this.attr(null),
      contactFormLead: this.attr(null),
      contactFormConcent: this.attr(null),

      // Trackers
      hasGoogleAnalytics: this.boolean(false),
      googleAnalyticsTrackingCode: this.attr(null),
      hasFacebookPixel: this.boolean(false),

      // Relationships
      sections: this.morphToMany(
        Section,
        Sectionable,
        'section_id',
        'sectionable_id',
        'sectionable_type'
      ),
      date: this.morphOne(Date, 'dateable_id', 'dateable_type'),
      illustrations: this.hasMany(Illustration, 'selector_id'),
      images: this.morphToMany(
        Image,
        Imageable,
        'image_id',
        'imageable_id',
        'imageable_type'
      ),
      projects: this.morphToMany(
        Project,
        Projectable,
        'project_id',
        'projectable_id',
        'projectable_type'
      ),
    };
  }

  static transformer(data) {
    const item = data.data;

    const selector = {};
    selector.id = item.id;
    selector.name = item.name;

    // Add date object to Selector object
    selector.date = {
      date: item.date,
      date_gmt: item.date_gmt,
      modified: item.modified,
      modified_gmt: item.modified_gmt,
    };

    if (item.project.length > 0) {
      selector.project_id = item.project[0];
      Selector.commit(state => {
        state.projectId = item.project[0];
      });
      selector.projects = [];
      item.project.forEach(project => {
        selector.projects.push({ id: project });
      });
    }

    if (item.acf.section_select === 'multiple') {
      selector.singleSection = false;

      if (item.acf.overview) {
        selector.overview_illustration_ids = [];
        selector.illustrations = [];

        item.acf.overview.forEach(item => {
          selector.illustrations.push({ id: item.illustration });
          selector.overview_illustration_ids.push(item.illustration);
        });
      }

      if (item.acf.sections) {
        selector.sections = [];
        selector.section_ids = [];
        selector.section_illustration_ids = {};
        item.acf.sections.forEach(section => {
          // Add sections array to selector.
          selector.section_ids.push(section.section.term_id);

          if (section.section_illustrations) {
            selector.section_illustration_ids[section.section.term_id] = [];
            section.section_illustrations.forEach(item => {
              selector.section_illustration_ids[section.section.term_id].push(
                item.illustration
              );
            });
          }

          let availabilityId = null;
          if (section.availability) {
            availabilityId = section.availability.term_id;
          }

          let shapeItems = [];
          if (section.shapes) {
            section.shapes.forEach(shape => {
              const shapeData = shape;
              shapeData.availability_id = section.availability.term_id;
              shapeData.availability = section.availability;
              shapeData.url = section.url;
              shapeData.type = 'sections';
              shapeItems.push(shapeData);
            });
          }

          selector.sections.push({
            id: section.section.term_id,
            shapes: shapeItems,
            availability: {
              id: availabilityId,
            },
            url: section.url,
          });
        });
      }
    } else {
      selector.singleSection = true;
      if (item.acf.section_single) {
        selector.overview_illustration_ids = [];
        selector.illustrations = [];
        item.acf.section_single.forEach(item => {
          selector.illustrations.push({ id: item.illustration });
          selector.overview_illustration_ids.push(item.illustration);
        });
      }
    }

    // View settings
    if (item.acf.illustration_show_check) {
      selector.showIllustration = JSON.parse(item.acf.illustration_show_check);
    }
    if (item.acf.list_view_check) {
      selector.listType = item.acf.list_view_check;
    }
    if (item.acf.banner_check) {
      selector.showBanner = JSON.parse(item.acf.banner_check);
      Selector.commit(state => {
        state.showBanner = JSON.parse(item.acf.banner_check);
      });
    }
    if (item.acf.image_size) {
      selector.cardImageSize = item.acf.image_size;
    }

    if (item.acf.colorsection_select) {
      selector.showColorOnSectionForSale = JSON.parse(
        item.acf.colorsection_select
      );
    }
    if (item.acf.colorresidence_select) {
      selector.showColorOnResidenceForSale = JSON.parse(
        item.acf.colorresidence_select
      );
    }

    if (item.acf.color_loader) {
      selector.colorLoader = item.acf.color_loader;
    }
    if (item.acf.color_buttons) {
      selector.colorButtons = item.acf.color_buttons;
    }
    if (item.acf.color_buttontext) {
      selector.colorButtonText = item.acf.color_buttontext;
    }
    if (item.acf.color_forsale) {
      selector.colorForSale = item.acf.color_forsale;
    }
    if (item.acf.color_reserved) {
      selector.colorReserved = item.acf.color_reserved;
    }
    if (item.acf.color_sold) {
      selector.colorSold = item.acf.color_sold;
    }
    if (item.acf.text_hovercolor) {
      selector.colorTextColor = JSON.parse(item.acf.text_hovercolor);
    }

    // Options
    if (item.acf.options_select !== 'false') {
      selector.hasOptions = JSON.parse(item.acf.options_select.toLowerCase());
    }

    // Calculator
    if (item.acf.calculator_select !== 'false') {
      selector.hasCalculator = JSON.parse(
        item.acf.calculator_select.toLowerCase()
      );

      if (item.acf.interest_rate_individual_select) {
        selector.interestRateIndividualSelect = JSON.parse(
          item.acf.interest_rate_individual_select.toLowerCase()
        );
      }
      if (item.acf.interest_rate_individual) {
        selector.interestRateIndividual = item.acf.interest_rate_individual;
      }
      if (item.acf.individual_loan_description) {
        selector.individualLoanDescription =
          item.acf.individual_loan_description;
      }

      if (item.acf.deposit_size) {
        selector.depositSize = item.acf.deposit_size;
      }
      if (item.acf.deposit_description) {
        selector.depositDescription = item.acf.deposit_description;
      }
      if (item.acf.communal_loan_description) {
        selector.communalLoanDescription = item.acf.communal_loan_description;
      }
      if (item.acf.communal_loan_years) {
        selector.communalLoanYears = parseFloat(item.acf.communal_loan_years);
      }

      if (item.acf.interest_rate_communal) {
        selector.interestRateComunal = item.acf.interest_rate_communal;
      }
      if (item.acf.interest_fee) {
        selector.interestFee = item.acf.interest_fee;
      }
      if (item.acf.interest_rate_communal_description) {
        selector.interestRateComunalDescription =
          item.acf.interest_rate_communal_description;
      }

      if (item.acf.custom_interest_rates) {
        selector.interestRateComunalExtras = item.acf.custom_interest_rates;
      }

      if (item.acf.installment_periods) {
        selector.installmentPeriods = item.acf.installment_periods;
      }

      if (item.acf.fixed_monthly_costs) {
        selector.fixedMonthlyCosts = item.acf.fixed_monthly_costs;
      }
      if (item.acf.flexible_monthly_costs) {
        selector.flexibleMonthlyCosts = item.acf.flexible_monthly_costs;
      }
      if (item.acf.monthly_costs_description) {
        selector.monthlyCostsDescription = item.acf.monthly_costs_description;
      }

      if (item.acf.tax_deduction_check) {
        selector.taxDeductionCheck = JSON.parse(
          item.acf.tax_deduction_check.toLowerCase()
        );
      }
      if (item.acf.tax_deduction_value) {
        selector.taxDeductionValue = item.acf.tax_deduction_value;
      }
      if (item.acf.tax_deduction_description) {
        selector.taxDeductionDescription = item.acf.tax_deduction_description;
      }
    }

    // Contact Form
    if (item.acf.contact_form_select !== 'false') {
      selector.hasContactForm = JSON.parse(
        item.acf.contact_form_select.toLowerCase()
      );
      if (item.acf.contact_form_title) {
        selector.contactFormTitle = item.acf.contact_form_title;
      }
      if (item.acf.contact_form_lead) {
        selector.contactFormLead = item.acf.contact_form_lead;
      }
      if (item.acf.contact_form_consent) {
        selector.contactFormConcent = item.acf.contact_form_consent;
      }
    }

    // Trackers
    if (item.acf.ga_check !== 'false') {
      selector.hasGoogleAnalytics = JSON.parse(item.acf.ga_check.toLowerCase());
    }
    selector.googleAnalyticsTrackingCode = item.acf.ga_tracking_code;
    if (item.acf.fb_check !== 'false') {
      selector.hasFacebookPixel = JSON.parse(item.acf.fb_check.toLowerCase());
    }

    return selector;
  }

  static apiConfig = {
    dataTransformer: data => this.transformer(data),
  };

  static fetch(selectorId, baseURL) {
    let url = '/wp-json/wp/v2/selector/';
    if (selectorId) {
      url += selectorId;
    }
    return this.api().get(url, {
      baseURL: baseURL,
    });
  }
}
